import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FolderOpen, Search } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { useCollectionStore } from '@/features/collections/stores/useCollectionStore';
import { useCollections } from '@/features/collections/context/CollectionsContext';
import { Separator } from '@/shared/components/ui/separator';
import { Badge } from '@/shared/components/ui/badge';
import collectionIcon from '@/assets/collection-icon.png';
import { CollectionFilter } from '@/features/collections/types/collection.types';
import Spinner from '@/shared/components/ui/spinner';
import CollectionCard from './Card';

const CollectionsPage = () => {
  const navigate = useNavigate();
  const loading = useCollectionStore(state => state.loading);
  const collections = useCollectionStore(state => state.collections);
  const setCurrentCollections = useCollectionStore(state => state.setCurrentCollections);
  const activeFilter = useCollectionStore(state => state.activeFilter);
  const setActiveFilter = useCollectionStore(state => state.setActiveFilter);
  const { addCollection, searchCollections } = useCollections();
  const [searchQuery, setSearchQuery] = useState('');

  // reset current page and collection on load and unload collectionSturcture
  useEffect(() => {
    return () => {
      void searchCollections('');
    };
  }, [searchCollections]);

  const filteredCollections = useMemo(() => {
    let filtered = collections.filter(c => {
      if (activeFilter === CollectionFilter.All) {
        return true;
      }
      if (c.isPublic && activeFilter === CollectionFilter.Public) {
        return true;
      }
      if (c.isFavourite && activeFilter === CollectionFilter.Favourites) {
        return true;
      }
      if (c.archivedAt && activeFilter === CollectionFilter.Archived) {
        return true;
      }
      return false;
    });
    setCurrentCollections(filtered);
    if (searchQuery) {
      filtered = filtered.filter(collection =>
        collection.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return filtered;
  }, [collections, setCurrentCollections, searchQuery, activeFilter]);

  const filterCollections = useCallback(
    (activeFilter: CollectionFilter) => {
      setActiveFilter(activeFilter);
    },
    [setActiveFilter]
  );

  const createCollection = useCallback(async () => {
    const coll = await addCollection();
    navigate(`/collection/${coll.id}`);
  }, [addCollection, navigate]);

  return (
    <div className="mx-auto md:max-w-3xl lg:max-w-6xl px-4 md:px-8 mb-12">
      <CollectionBanner />

      <CollectionActions
        activeFilter={activeFilter}
        filterCollections={filterCollections}
        searchQuery={searchQuery}
        showArchived={true}
        setSearchQuery={query => {
          setSearchQuery(query);
          void searchCollections(query);
        }}
        addCollection={createCollection}
      />

      <Separator className="mt-6 mb-8" />

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 mb-12">
        {filteredCollections.map(collection => (
          <CollectionCard key={collection.id} collection={collection} />
        ))}
      </div>

      {filteredCollections.length === 0 && (
        <NoCollections
          searchQuery={searchQuery}
          filter={activeFilter}
          loading={loading}
          createCollection={createCollection}
        />
      )}
    </div>
  );
};

export default CollectionsPage;

const CollectionActions = ({
  activeFilter,
  filterCollections,
  searchQuery,
  setSearchQuery,
  addCollection,
  showArchived,
}: {
  activeFilter: CollectionFilter;
  filterCollections: (activeFilter: CollectionFilter) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  addCollection: () => void;
  showArchived: boolean;
}) => {
  return (
    <>
      {/* Filter Chips */}
      <div className="pt-2 pb-6 overflow-x-auto">
        <div className="flex gap-2">
          <Badge
            variant={activeFilter === CollectionFilter.All ? 'default' : 'outline'}
            className="cursor-pointer px-4 py-2 text-sm font-medium"
            onClick={() => {
              filterCollections(CollectionFilter.All);
            }}
          >
            All
          </Badge>
          <Badge
            variant={activeFilter === CollectionFilter.Favourites ? 'default' : 'outline'}
            className="cursor-pointer px-4 py-2 text-sm font-medium"
            onClick={() => {
              filterCollections(CollectionFilter.Favourites);
            }}
          >
            Favourites
          </Badge>
          <Badge
            variant={activeFilter === CollectionFilter.Public ? 'default' : 'outline'}
            className="cursor-pointer px-4 py-2 text-sm font-medium"
            onClick={() => {
              filterCollections(CollectionFilter.Public);
            }}
          >
            Public
          </Badge>
          {showArchived && (
            <Badge
              variant={activeFilter === CollectionFilter.Archived ? 'default' : 'outline'}
              className="cursor-pointer px-4 py-2 text-sm font-medium"
              onClick={() => {
                filterCollections(CollectionFilter.Archived);
              }}
            >
              Archived
            </Badge>
          )}
        </div>
      </div>
      <div className="flex justify-between items-center gap-2 mb-6">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search collections..."
            value={searchQuery}
            onChange={e => {
              setSearchQuery(e.target.value);
            }}
            className="pl-10 pr-4 h-10 rounded-full"
          />
        </div>
        <Button onClick={addCollection} className="flex items-center gap-2 rounded-full">
          <Plus size={16} />
          <span className="hidden sm:block">New Collecton</span>
        </Button>
      </div>
    </>
  );
};

export const NoCollections = ({
  searchQuery,
  filter = CollectionFilter.All,
  loading,
  createCollection,
}: {
  searchQuery?: string;
  filter?: CollectionFilter;
  loading?: boolean;
  createCollection?: () => void;
}) => {
  if (loading) {
    return <Spinner size={26} />;
  }
  return (
    <div className="text-center flex-col justify-center mx-auto">
      <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
      <h3 className="text-lg font-medium mb-2">
        No collections {filter === CollectionFilter.All ? 'yet' : ''}{' '}
        {filter !== CollectionFilter.All ? `under "${filter}"` : ''}
      </h3>
      <p className="text-muted-foreground">
        {searchQuery
          ? 'Try a different search term'
          : filter === CollectionFilter.All
            ? 'Get started with you first collection'
            : ''}
      </p>
      {filter === CollectionFilter.All && !searchQuery && (
        <div className="w-full flex justify-center mt-6">
          <Button
            size="lg"
            className="flex items-center gap-2 bg-primary"
            onClick={createCollection}
          >
            <Plus className="mr-2 h-4 w-4" />
            Start Collection
          </Button>
        </div>
      )}
    </div>
  );
};

export const CollectionBanner = () => {
  return (
    <section className="flex flex-col md:flex-row justify-between items-center gap-12 mb-6">
      <div className="md:w-1/2">
        <h1 className="text-2xl md:text-4xl font-extrabold leading-tight mb-4">
          Build a home for everything that interests you
        </h1>
        <p className="text-muted-foreground text-md md:text-lg max-w-lg">
          Group related notes to stay organized. Perfect for projects, ideas, or any notes that
          belong together.
        </p>
      </div>

      <div className="sm:w-1/3 pt-4 h-52">
        <img
          src={collectionIcon}
          alt="Collection dashboard preview"
          className="rounded-lg w-64 h-auto sm:w-full min-h-32 object-cover"
          onError={e => {
            console.error('Image failed to load:', e);
          }}
        />
      </div>
    </section>
  );
};
